import React, { useCallback } from 'react';

import { Typography } from '@rbilabs/universal-components';
import { Image, ImageBackground } from 'expo-image';
import { useAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { menuSectionStyles } from 'features/menu/components/menu-section/styled';
import { IMenuSectionItemProps } from 'features/menu/components/menu-section/types';
import { buildMenuLink } from 'features/menu/utils/navigation';
import { traceFirstMenuItemLoaded } from 'features/menu/utils/performance';
import { View } from 'react-native';

import Link from 'components/link';
import Picture from 'components/picture';
import { useFormatCalories } from 'hooks/use-format-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';

// NEW one menu
const _MenuSectionListItem: React.FC<IMenuSectionItemProps> = ({ item }) => {
  const { formatCurrencyForLocale } = useUIContext();
  const formatCalories = useFormatCalories();

  const { menu: menuAppTakeover, pdp: pdpAppTakeover, fontFamily } = useAppTakeover();
  const takeoverSection = menuAppTakeover?.targetSectionIds?.[item.id];
  const takeoverItem = pdpAppTakeover?.targetMenuItemIds?.[item.id];
  const isTakeoverItem = !!takeoverItem;

  const fontColorOverride = takeoverSection?.fontColor || takeoverItem?.sectionFontColor;

  const linkPath = buildMenuLink(item);

  const handleLinkClick = useCallback(() => {
    const isProduct = !!item.product;
    const attributes = {
      component: isProduct
        ? ClickEventComponentNames.PRODUCTS
        : ClickEventComponentNames.PRODUCT_SECTION_TILES,
      name: item.name,
      componentId: item.id,
    };
    logButtonClick({ attributes });

    startTrace(isProduct ? TraceName.MENU_PDP_LOAD : TraceName.MENU_SECTION_LOAD, {
      menuItemId: item.id,
    });
  }, [item]);

  const onImageDisplay = useCallback(() => {
    traceFirstMenuItemLoaded({ menuItemId: item.id, isOneMenu: 'true' });
  }, [item.id]);

  const Element =
    takeoverSection?.sectionTileBackgroundImage || takeoverItem?.sectionBackgroundImage
      ? ImageBackground
      : View;

  return (
    <Link key={item.id} onPress={handleLinkClick} linkPath={linkPath} dd-action-name={item.name}>
      <Element
        source={takeoverSection?.sectionTileBackgroundImage || takeoverItem?.sectionBackgroundImage}
        style={[
          menuSectionStyles.menuSectionRow,
          {
            backgroundColor: isTakeoverItem
              ? takeoverSection?.itemBackgroundColor || takeoverItem?.sectionBackgroundColor
              : primitive.$white,
          },
        ]}
        testID="menu-tile-bk"
        dataSet={{ type: item.type, itemId: item.id }}
      >
        <View style={menuSectionStyles.menuSectionImageWrapper}>
          <Picture
            image={item.image.resource}
            alt={item.image.description}
            onDisplay={onImageDisplay}
            accessibilityHint={t('select')}
            style={menuSectionStyles.menuSectionImage}
          />
        </View>

        <View style={menuSectionStyles.menuSectionContent}>
          <Typography
            variant="headerTwo"
            mb="$0"
            color={fontColorOverride}
            style={isTakeoverItem && fontFamily ? { fontFamily } : undefined}
            testID={`tile-header-${item.name?.toLowerCase().replace(/\s/g, '')}`}
          >
            {item.name}
          </Typography>
          {item.product && (
            <View style={menuSectionStyles.menuSectionProductWrapper}>
              <Typography bold mr="$3" color={fontColorOverride}>
                {formatCurrencyForLocale(item.product.price)}
              </Typography>
              <Typography numberOfLines={1} color={fontColorOverride}>
                {formatCalories(item.product.calories)}
              </Typography>
            </View>
          )}
        </View>
        {takeoverSection?.sectionTileImage && (
          <Image
            pointerEvents="box-none"
            source={takeoverSection.sectionTileImage}
            contentFit="contain"
            style={[{ marginLeft: 'auto' }, takeoverSection.sectionTileImageStyle]}
          />
        )}
      </Element>
    </Link>
  );
};

export const MenuSectionListItem = React.memo(_MenuSectionListItem);
